/**
 * Saisie par matière — examens (mode tronc commun multi-options).
 *
 * Même logique que la saisie par matière des devoirs, adaptée à l'examen :
 *  1. Choix d'un NOM de matière (une même matière existe sur plusieurs options).
 *  2. Verrouillage : un mapping "code_classe → matiere_id" est pré-calculé.
 *  3. Recherche d'un étudiant par nom OU matricule, toutes classes confondues.
 *  4. Sélection : la classe est auto-détectée → matiere_id correspondant.
 *  5. Saisie de la note d'examen (ou AB) → Entrée pour sauvegarder et enchaîner.
 *  6. Liste vivante des saisies récentes.
 *
 * Différences avec les devoirs : une seule note au lieu de D1/D2, et un
 * basculement session ordinaire / rattrapage.
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Lock, Unlock, Search, Save, Loader, CheckCircle2, XCircle,
  BookOpen, User, Trash2, RotateCcw,
} from 'lucide-react';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useSaisieIndividuelleMutation, useGetNotesExamensQuery } from '../../services/notesExamensApi';
import { useGetNotesDevoirsQuery } from '../../services/notesDevoirsApi';
import type { Matiere } from '../../services/matieresApi';
import type { Etudiant } from '../../services/etudiantsApi';

interface Props {
  anneeAcademique: string;
}

interface SaisieRecente {
  id: string;
  etudiant: Etudiant;
  matiere: Matiere;
  semestre: 'S1' | 'S2';
  note: number | null;
  absent: boolean;
  rattrapage: boolean;
  status: 'pending' | 'success' | 'error';
  message?: string;
  timestamp: number;
}

const SaisieParMatiereExamens: React.FC<Props> = ({ anneeAcademique }) => {
  // ── 1. Sélection matière ──────────────────────────────────────────────────
  const { data: allMatieres = [], isLoading: loadingMatieres } = useGetMatieresQuery();
  const [matiereSearch, setMatiereSearch] = useState('');
  const [selectedNom, setSelectedNom] = useState<string | null>(null);
  const [selectedNiveau, setSelectedNiveau] = useState<string>('');
  const [selectedSemestre, setSelectedSemestre] = useState<'S1' | 'S2'>('S1');
  const [estRattrapage, setEstRattrapage] = useState(false);
  const [matiereLocked, setMatiereLocked] = useState(false);

  // Groupement par (nom_matiere + niveau + semestre) — chaque groupe contient
  // les variantes de la matière sur les différentes options/classes.
  const groupes = useMemo(() => {
    const map = new Map<string, { nom: string; niveau: string; semestre: number; matieres: Matiere[] }>();
    for (const m of allMatieres) {
      if (!m.actif || !m.nom_matiere) continue;
      const niv = m.niveau || '';
      const sem = m.semestre || 0;
      const key = `${m.nom_matiere}::${niv}::${sem}`;
      if (!map.has(key)) {
        map.set(key, { nom: m.nom_matiere, niveau: niv, semestre: sem, matieres: [] });
      }
      map.get(key)!.matieres.push(m);
    }
    return Array.from(map.values()).sort((a, b) => {
      const c1 = a.niveau.localeCompare(b.niveau);
      if (c1 !== 0) return c1;
      const c2 = a.semestre - b.semestre;
      if (c2 !== 0) return c2;
      return a.nom.localeCompare(b.nom);
    });
  }, [allMatieres]);

  const groupesFiltres = useMemo(() => {
    let res = groupes;
    if (selectedNiveau) res = res.filter(g => g.niveau === selectedNiveau);
    if (selectedSemestre === 'S1') {
      res = res.filter(g => [1, 3, 5].includes(g.semestre));
    } else {
      res = res.filter(g => [2, 4, 6].includes(g.semestre));
    }
    if (matiereSearch.trim()) {
      const q = matiereSearch.trim().toLowerCase();
      res = res.filter(g => g.nom.toLowerCase().includes(q));
    }
    return res.slice(0, 30);
  }, [groupes, selectedNiveau, selectedSemestre, matiereSearch]);

  const groupeSelectionne = useMemo(() => {
    if (!selectedNom || !selectedNiveau) return null;
    return groupes.find(g =>
      g.nom === selectedNom &&
      g.niveau === selectedNiveau &&
      ((selectedSemestre === 'S1' && [1, 3, 5].includes(g.semestre)) ||
       (selectedSemestre === 'S2' && [2, 4, 6].includes(g.semestre)))
    ) || null;
  }, [selectedNom, selectedNiveau, selectedSemestre, groupes]);

  const classeVersMatiere = useMemo(() => {
    const map = new Map<string, Matiere>();
    if (!groupeSelectionne) return map;
    for (const m of groupeSelectionne.matieres) {
      if (m.classe) map.set(m.classe.toUpperCase(), m);
    }
    return map;
  }, [groupeSelectionne]);

  // ── 2. Recherche étudiant ─────────────────────────────────────────────────
  const [etudiantSearch, setEtudiantSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(etudiantSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [etudiantSearch]);

  const { data: etudiantsRecherches = [], isFetching: searching } = useGetEtudiantsQuery(
    { search: debouncedSearch, actif: true, page_size: 30 },
    { skip: !matiereLocked || debouncedSearch.length < 2 }
  );

  const candidats = useMemo(() => {
    if (!matiereLocked) return [];
    const classesValides = new Set(Array.from(classeVersMatiere.keys()));
    const enriched = etudiantsRecherches.map(e => ({
      etudiant: e,
      eligible: !!(e.classe && classesValides.has(e.classe.toUpperCase())),
    }));
    enriched.sort((a, b) => {
      if (a.eligible !== b.eligible) return a.eligible ? -1 : 1;
      return (a.etudiant.nom || '').localeCompare(b.etudiant.nom || '');
    });
    return enriched.slice(0, 20);
  }, [etudiantsRecherches, classeVersMatiere, matiereLocked]);

  const nbEligibles = candidats.filter(c => c.eligible).length;

  // ── 3. Étudiant sélectionné + saisie ─────────────────────────────────────
  const [selectedEtu, setSelectedEtu] = useState<Etudiant | null>(null);
  const [note, setNote] = useState<string>('');
  const [absent, setAbsent] = useState(false);
  const noteRef = useRef<HTMLInputElement>(null);

  const matiereCible = useMemo(() => {
    if (!selectedEtu?.classe) return null;
    return classeVersMatiere.get(selectedEtu.classe.toUpperCase()) || null;
  }, [selectedEtu, classeVersMatiere]);

  // Notes déjà saisies, pour comparaison avant d'écraser
  const { data: existingExamens = [] } = useGetNotesExamensQuery(
    selectedEtu && matiereCible
      ? {
          etudiant_id: selectedEtu.id,
          matiere_id: matiereCible.id,
          annee_academique: anneeAcademique,
          semestre: selectedSemestre,
        }
      : ({} as any),
    { skip: !selectedEtu || !matiereCible }
  );
  const examenOrdinaire = existingExamens.find(n => n.semestre === selectedSemestre && !n.est_rattrapage);
  const examenRattrapage = existingExamens.find(n => n.semestre === selectedSemestre && n.est_rattrapage);
  const noteExistante = estRattrapage ? examenRattrapage : examenOrdinaire;

  const { data: existingDevoirs = [] } = useGetNotesDevoirsQuery(
    selectedEtu && matiereCible
      ? {
          etudiant_id: selectedEtu.id,
          matiere_id: matiereCible.id,
          annee_academique: anneeAcademique,
          semestre: selectedSemestre,
        }
      : ({} as any),
    { skip: !selectedEtu || !matiereCible }
  );
  const noteDevoirExistante = existingDevoirs.find(n => n.semestre === selectedSemestre);

  useEffect(() => {
    if (selectedEtu && noteRef.current) {
      setTimeout(() => noteRef.current?.focus(), 50);
    }
  }, [selectedEtu]);

  // ── 4. Mutation + historique ──────────────────────────────────────────────
  const [saisieIndiv, { isLoading: saving }] = useSaisieIndividuelleMutation();
  const [recentes, setRecentes] = useState<SaisieRecente[]>([]);
  const [globalMessage, setGlobalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const resetSaisie = () => {
    setSelectedEtu(null);
    setNote('');
    setAbsent(false);
    setEtudiantSearch('');
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSauvegarder = useCallback(async () => {
    if (!selectedEtu || !matiereCible) return;
    const n = note.trim() ? parseFloat(note) : null;

    if (n !== null && (isNaN(n) || n < 0 || n > 20)) {
      setGlobalMessage({ type: 'error', text: `Note invalide : ${note} (doit être entre 0 et 20)` });
      setTimeout(() => setGlobalMessage(null), 3000);
      return;
    }
    if (n === null && !absent) {
      setGlobalMessage({ type: 'error', text: 'Saisis une note, ou coche AB.' });
      setTimeout(() => setGlobalMessage(null), 3000);
      return;
    }

    const localId = `${selectedEtu.id}_${Date.now()}`;
    const entry: SaisieRecente = {
      id: localId,
      etudiant: selectedEtu,
      matiere: matiereCible,
      semestre: selectedSemestre,
      note: n,
      absent,
      rattrapage: estRattrapage,
      status: 'pending',
      timestamp: Date.now(),
    };
    setRecentes(prev => [entry, ...prev].slice(0, 30));

    try {
      await saisieIndiv({
        etudiant_id: selectedEtu.id,
        matiere_id: matiereCible.id,
        annee_academique: anneeAcademique,
        semestre: selectedSemestre,
        note_examen: absent ? null : n,
        absent,
        est_rattrapage: estRattrapage,
      }).unwrap();
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'success', message: 'Sauvegardé' } : r
      ));
      setGlobalMessage({ type: 'success', text: `${selectedEtu.matricule} (${selectedEtu.classe}) — sauvegardé ✓` });
      setTimeout(() => setGlobalMessage(null), 1500);
      resetSaisie();
    } catch (e: any) {
      const msg = e?.data?.error || e?.data?.detail || e?.message || 'Erreur lors de la sauvegarde';
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'error', message: msg } : r
      ));
      setGlobalMessage({ type: 'error', text: msg });
    }
  }, [selectedEtu, matiereCible, note, absent, estRattrapage, selectedSemestre, anneeAcademique, saisieIndiv]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSauvegarder();
    }
  };

  // ── Rendu : matière pas encore verrouillée ───────────────────────────────
  if (!matiereLocked || !groupeSelectionne) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-3xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
          <BookOpen className="text-blue-600" size={22} />
          Saisie par matière — examens
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Choisis une matière pour la verrouiller, puis saisis les notes d'examen étudiant par étudiant —
          quelle que soit leur classe. Idéal pour les matières en tronc commun.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            >
              <option value="">— Choisir —</option>
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
            <select
              value={selectedSemestre}
              onChange={(e) => setSelectedSemestre(e.target.value as 'S1' | 'S2')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            >
              <option value="S1">Semestre 1</option>
              <option value="S2">Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Année</label>
            <input
              type="text"
              value={anneeAcademique}
              readOnly
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-700"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={estRattrapage}
            onChange={(e) => setEstRattrapage(e.target.checked)}
            className="w-4 h-4"
          />
          <RotateCcw size={14} className={estRattrapage ? 'text-orange-600' : 'text-gray-400'} />
          Session de rattrapage
          <span className="text-xs text-gray-500">
            (décoché = session ordinaire)
          </span>
        </label>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Matière
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={matiereSearch}
              onChange={(e) => setMatiereSearch(e.target.value)}
              placeholder="Rechercher une matière…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
          </div>
        </div>

        {loadingMatieres ? (
          <div className="text-center py-6 text-gray-500 flex items-center justify-center gap-2">
            <Loader size={16} className="animate-spin" /> Chargement des matières…
          </div>
        ) : !selectedNiveau ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Choisis d'abord un niveau.
          </div>
        ) : groupesFiltres.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Aucune matière ne correspond.
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded divide-y dark:divide-gray-700 max-h-80 overflow-y-auto">
            {groupesFiltres.map(g => (
              <button
                key={`${g.nom}::${g.niveau}::${g.semestre}`}
                onClick={() => {
                  setSelectedNom(g.nom);
                  setMatiereLocked(true);
                  setTimeout(() => searchInputRef.current?.focus(), 80);
                }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-between gap-2"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{g.nom}</div>
                  <div className="text-xs text-gray-500">
                    {g.niveau} · semestre {g.semestre} · {g.matieres.length} option(s)
                  </div>
                </div>
                <Lock size={14} className="text-gray-400 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Rendu : matière verrouillée ──────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* En-tête : matière verrouillée */}
      <div className={`border rounded-lg p-3 flex items-center gap-3 flex-wrap ${
        estRattrapage
          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
      }`}>
        <Lock className={estRattrapage ? 'text-orange-600' : 'text-blue-600'} size={20} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {groupeSelectionne.nom}
            {estRattrapage && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100 rounded uppercase">
                Rattrapage
              </span>
            )}
          </div>
          <div className="text-xs text-gray-700 dark:text-gray-300">
            {groupeSelectionne.niveau} {selectedSemestre} · {groupeSelectionne.matieres.length} option(s) couverte(s) :{' '}
            {Array.from(classeVersMatiere.keys()).join(', ')}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <span>{anneeAcademique}</span>
          <button
            onClick={() => {
              setMatiereLocked(false);
              setSelectedNom(null);
              setSelectedEtu(null);
              setEtudiantSearch('');
              setRecentes([]);
            }}
            className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100"
            title="Déverrouiller pour changer de matière"
          >
            <Unlock size={12} /> Changer
          </button>
        </div>
      </div>

      {/* Barre de saisie */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-start">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Étudiant (matricule ou nom)
          </label>
          {selectedEtu ? (
            <div className="p-2 border border-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded space-y-2">
              <div className="flex items-center gap-2">
                <User size={16} className="text-blue-600 shrink-0" />
                <div className="flex-1">
                  <div className="font-mono font-bold text-sm text-gray-900 dark:text-gray-100">
                    {selectedEtu.matricule}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {selectedEtu.nom} {selectedEtu.prenoms}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Classe : <span className="font-semibold text-blue-700 dark:text-blue-300">{selectedEtu.classe}</span>
                    {matiereCible && (
                      <>
                        {' · matière_id : '}
                        <span className="font-mono text-[10px]">{matiereCible.id} ({matiereCible.code_matiere})</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={resetSaisie}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2"
                  title="Retirer l'étudiant et chercher un autre"
                >
                  <XCircle size={16} />
                </button>
              </div>

              {!matiereCible && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded p-1.5 text-[11px] text-red-700 dark:text-red-300">
                  Cette matière n'existe pas dans la maquette de {selectedEtu.classe} : la note ne peut pas être enregistrée.
                </div>
              )}

              {(noteDevoirExistante || examenOrdinaire || examenRattrapage) && (
                <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded p-1.5 text-[11px] flex items-center gap-3 flex-wrap">
                  <span className="font-semibold text-amber-800 dark:text-amber-300">
                    Déjà saisi :
                  </span>
                  {noteDevoirExistante && (
                    <span className="text-purple-700 dark:text-purple-300">
                      D1 <strong>{noteDevoirExistante.absent1 ? 'AB' : (noteDevoirExistante.note1 ?? '—')}</strong>
                      {' · '}
                      D2 <strong>{noteDevoirExistante.absent2 ? 'AB' : (noteDevoirExistante.note2 ?? '—')}</strong>
                    </span>
                  )}
                  {examenOrdinaire && (
                    <span className="text-blue-700 dark:text-blue-300">
                      Examen <strong>{examenOrdinaire.absent ? 'AB' : (examenOrdinaire.note_examen ?? '—')}</strong>
                    </span>
                  )}
                  {examenRattrapage && (
                    <span className="text-orange-700 dark:text-orange-300">
                      Rattrapage <strong>{examenRattrapage.absent ? 'AB' : (examenRattrapage.note_examen ?? '—')}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={etudiantSearch}
                  onChange={(e) => setEtudiantSearch(e.target.value)}
                  placeholder="Matricule ou nom…"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
                {searching && (
                  <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
              {debouncedSearch.length >= 2 && (
                <div className="mt-1 border border-gray-200 dark:border-gray-700 rounded divide-y dark:divide-gray-700 max-h-64 overflow-y-auto">
                  {candidats.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500">Aucun étudiant trouvé.</div>
                  ) : (
                    <>
                      {nbEligibles === 0 && (
                        <div className="px-3 py-1.5 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20">
                          Aucun de ces étudiants ne suit cette matière.
                        </div>
                      )}
                      {candidats.map(({ etudiant, eligible }) => (
                        <button
                          key={etudiant.id}
                          disabled={!eligible}
                          onClick={() => setSelectedEtu(etudiant)}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                            eligible
                              ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              : 'opacity-40 cursor-not-allowed'
                          }`}
                          title={eligible ? '' : `${etudiant.classe} ne suit pas cette matière`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-xs font-bold shrink-0">{etudiant.matricule}</span>
                            <span className="text-sm truncate">{etudiant.nom} {etudiant.prenoms}</span>
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded shrink-0">
                            {etudiant.classe}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Champs note */}
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-blue-700 mb-1">
              {estRattrapage ? 'Note rattrapage' : 'Note examen'}
            </label>
            <input
              ref={noteRef}
              type="number" min="0" max="20" step="0.25"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!selectedEtu || absent || !matiereCible}
              placeholder="—"
              className="w-24 px-2 py-2 text-base font-bold text-center border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 dark:disabled:bg-gray-900"
            />
          </div>

          <label className="flex items-center gap-1 mb-2 text-xs text-gray-700 dark:text-gray-300 self-end">
            <input
              type="checkbox"
              checked={absent}
              onChange={(e) => { setAbsent(e.target.checked); if (e.target.checked) setNote(''); }}
              disabled={!selectedEtu}
              className="w-4 h-4"
            />
            AB
          </label>

          <button
            onClick={handleSauvegarder}
            disabled={!selectedEtu || !matiereCible || saving}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            title="Entrée pour sauvegarder"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Message global */}
      {globalMessage && (
        <div className={`p-2 rounded text-sm flex items-center gap-2 ${
          globalMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
        }`}>
          {globalMessage.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {globalMessage.text}
        </div>
      )}

      {/* Saisies récentes */}
      {recentes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-2 border-b dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Saisies récentes ({recentes.length})
            </h3>
            <button
              onClick={() => setRecentes([])}
              className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={11} /> Effacer la liste
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Heure</th>
                  <th className="text-left px-3 py-1.5 font-medium">Matricule</th>
                  <th className="text-left px-3 py-1.5 font-medium">Nom</th>
                  <th className="text-left px-3 py-1.5 font-medium">Classe</th>
                  <th className="text-center px-3 py-1.5 font-medium">Note</th>
                  <th className="text-center px-3 py-1.5 font-medium">Session</th>
                  <th className="text-left px-3 py-1.5 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {recentes.map(r => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-1.5 text-gray-500 font-mono">
                      {new Date(r.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td className="px-3 py-1.5 font-mono font-semibold">{r.etudiant.matricule}</td>
                    <td className="px-3 py-1.5">{r.etudiant.nom} {r.etudiant.prenoms}</td>
                    <td className="px-3 py-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {r.etudiant.classe}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold">
                      {r.absent ? <span className="text-orange-600">AB</span> : (r.note != null ? r.note.toFixed(2) : '—')}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {r.rattrapage
                        ? <span className="text-orange-600 font-semibold">Rattrapage</span>
                        : <span className="text-gray-500">Ordinaire</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.status === 'pending' && <span className="text-gray-500 flex items-center gap-1"><Loader size={11} className="animate-spin" /> En cours…</span>}
                      {r.status === 'success' && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={11} /> {r.message}</span>}
                      {r.status === 'error' && <span className="text-red-600 flex items-center gap-1" title={r.message}><XCircle size={11} /> Erreur</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
        Tape le matricule ou le nom, sélectionne dans la liste, saisis la note et appuie sur{' '}
        <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono">Entrée</kbd> pour
        sauvegarder et passer au suivant.
      </div>
    </div>
  );
};

export default SaisieParMatiereExamens;
